import {
  type DatabaseView,
  DEFAULT_DATABASE_VIEW_SETTINGS,
} from '@databases/types/database/view';
import { pick, isEqual, debounce, pickBy } from 'lodash-es';
import { proxy, snapshot, subscribe } from 'valtio';
import { devtools } from 'valtio/utils';

export const viewState = proxy<{ view: DatabaseView }>({
  view: {
    id: '',
    name: '',
    settings: DEFAULT_DATABASE_VIEW_SETTINGS,
  },
});

export const setView = (view: DatabaseView) => {
  viewState.view = view;
};

let cachedView: DatabaseView | undefined;
let unsubscribe: (() => void) | undefined;

/**
 * Compares objects removing properties which have value 'undefined'.
 * This is a duplicate function to avoid circular dependency with @databases/utils.
 */
function isEqualOmitUndefined<T>(value: T, other: T): boolean {
  return isEqual(pickBy(value as any, undefined), pickBy(other as any, undefined));
}

/**
 * Returns a cached snapshot of the current database view settings.
 *
 * **Why this exists:**
 * `snapshot()` creates a new object reference every time it's called, even when data hasn't changed.
 * This breaks Redux selector memoization (re-reselect), causing unnecessary recalculations.
 * This function caches the snapshot and updates it via subscription, maintaining referential equality.
 *
 * **Implementation note:**
 * Uses lazy initialization to avoid test failures when valtio is mocked at module load time.
 */
export const getCachedViewSnapshot = (): DatabaseView => {
  if (!unsubscribe && subscribe) {
    unsubscribe = subscribe(viewState.view, () => {
      cachedView = snapshot(viewState.view) as DatabaseView;
    });
  }

  const currentView = snapshot(viewState.view) as DatabaseView;

  if (!cachedView) {
    cachedView = currentView;
    return cachedView;
  }

  if (
    cachedView.id !== currentView.id ||
    !isEqualOmitUndefined(cachedView.settings, currentView.settings)
  ) {
    cachedView = currentView;
  }

  return cachedView;
};

if (process.env.NODE_ENV === 'development') {
  devtools(viewState, 'Valtio View State');
}

let selectionsCachedView: DatabaseView | undefined;
let selectionsCacheUnsubscribe: (() => void) | undefined;

/**
 * In the selections API, when we build the adapter or actions for the action bar / context menu,
 * we need the most up to date state that busts whenever anything on the entire viewState changes.
 */
export const getCachedViewSnapshotForSelections = (): DatabaseView => {
  if (!selectionsCacheUnsubscribe && subscribe) {
    selectionsCacheUnsubscribe = subscribe(viewState, () => {
      selectionsCachedView = snapshot(viewState.view) as DatabaseView;
    });
  }

  if (!selectionsCachedView) {
    selectionsCachedView = snapshot(viewState.view) as DatabaseView;
  }

  return selectionsCachedView;
};

/**
 * Creates a lightweight subscription to relevant keys to listen to
 * to allow integration with the view input for selections. This allows
 * us to nuke selections when relevant view changes happen, but not all
 * view changes.
 */
export function createViewStateSubscription(onChange: VoidFunction) {
  const debouncedOnChange = debounce(onChange, 50);
  const SETTINGS_KEYS_TO_WATCH = [
    'sort',
    'sorts',
    'filter',
    'fields',
    'calculations',
    'grouping',
  ] as const;

  const getTrackedState = () => {
    const snap = snapshot(viewState.view);

    return {
      id: snap.id,
      settings: pick(snap.settings, SETTINGS_KEYS_TO_WATCH),
    };
  };

  let prevState = getTrackedState();

  const unsubscribe = subscribe(viewState, () => {
    const currentState = getTrackedState();

    // Fire if the ID changed OR if the specific settings changed
    if (
      prevState.id !== currentState.id ||
      !isEqual(prevState.settings, currentState.settings)
    ) {
      prevState = currentState;

      debouncedOnChange();
    }
  });

  return () => {
    unsubscribe();
    debouncedOnChange.cancel();
  };
}
